import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Phone } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import NotFound from "@/pages/NotFound";
import { Reveal, FadeUp } from "@/components/site/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CONTACT } from "@/data/content";
import { SUBURBS } from "@/data/suburbs";
import { useSiteContent } from "@/context/ContentContext";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { categories, services } = useSiteContent();
  const cat = categories.find((c) => c.id === slug);
  const detail = services[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!cat || !detail) return <NotFound />;

  const related = categories.filter((c) => c.id !== slug);
  const breadcrumb = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: cat.title, path: `/services/${slug}` },
  ];

  return (
    <SmoothScroll>
      <Seo pageKey={slug} service={{ name: cat.title }} faqs={detail.faqs} breadcrumb={breadcrumb} />
      <div className="grain-overlay" aria-hidden />
      <div data-testid={`service-page-${slug}`} className="bg-[#f8fafc] min-h-screen">
        <Navbar />

        {/* Hero */}
        <section className="relative h-[72vh] min-h-[520px] w-full overflow-hidden" data-testid="service-hero">
          <img src={cat.image} alt={`${cat.title} tiling in Perth`} fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#f8fafc] via-[#f8fafc]/45 to-transparent" />
          <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-end pb-16">
            <nav className="mb-8 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#64748b]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#9c7a4d] transition-colors">Home</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-[#9c7a4d] transition-colors">Services</Link>
              <span>/</span>
              <span className="text-[#0f172a]">{cat.title}</span>
            </nav>
            <p className="overline !text-[#9c7a4d] mb-4">{detail.heroKicker}</p>
            <h1 className="font-serif font-light text-[#0f172a] text-4xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-4xl">
              <Reveal load>{detail.heroTitle}</Reveal>
            </h1>
          </div>
        </section>

        {/* Overview + Included */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-7">
              <p className="overline !text-[#9c7a4d] mb-6">Overview</p>
              {detail.overview.map((p, i) => (
                <FadeUp key={i} delay={i * 0.06}>
                  <p className="text-[#475569] text-lg md:text-xl font-light leading-relaxed mb-6">{p}</p>
                </FadeUp>
              ))}
              <FadeUp delay={0.1}>
                <a
                  href="/#enquire"
                  data-testid="service-enquire-cta"
                  className="group inline-flex items-center gap-3 mt-4 bg-[#9c7a4d] text-white hover:bg-[#0f172a] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors duration-400"
                >
                  Get a Free Quote
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </FadeUp>
            </div>

            <div className="lg:col-span-5">
              <div className="border border-[#0f172a]/10 bg-white shadow-[0_20px_60px_-30px_rgba(33,29,24,0.35)] p-8 md:p-10">
                <p className="overline !text-[#94a3b8] mb-6">What's Included</p>
                <ul className="space-y-4">
                  {detail.included.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-[#9c7a4d] shrink-0 mt-0.5" />
                      <span className="text-[#1e293b] font-light">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-8 border-t border-[#0f172a]/10">
                  <p className="overline !text-[#94a3b8] mb-3">Speak to us</p>
                  <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-3 text-[#0f172a] hover:text-[#9c7a4d] transition-colors text-lg">
                    <Phone className="h-4 w-4 text-[#9c7a4d]" /> {CONTACT.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="pb-20 md:pb-28">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {detail.gallery.map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-8%" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden group aspect-[4/5]"
              >
                <img src={src} alt={`${cat.title} example ${i + 1}`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 ring-1 ring-inset ring-[#0f172a]/10" />
              </motion.div>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 md:py-28 bg-[#f1f5f9] border-y border-[#0f172a]/10" data-testid="service-faqs">
          <div className="mx-auto max-w-[1000px] px-6 md:px-12">
            <p className="overline !text-[#9c7a4d] mb-6">Frequently Asked</p>
            <h2 className="font-serif font-light text-[#0f172a] text-3xl md:text-5xl leading-tight tracking-tight mb-12">
              <Reveal>{cat.title} questions,</Reveal>
              <Reveal delay={0.08}>answered.</Reveal>
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {detail.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-[#0f172a]/10" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-[#0f172a] font-serif text-xl md:text-2xl font-light hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#64748b] font-light text-base md:text-lg leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Areas we serve — service/suburb internal linking */}
        <section className="py-16 md:py-20 border-b border-[#0f172a]/10" data-testid="service-areas">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
              <div>
                <p className="overline !text-[#9c7a4d] mb-4">Areas We Serve</p>
                <h2 className="font-serif font-light text-[#0f172a] text-2xl md:text-3xl leading-tight">
                  {cat.title} across Perth's suburbs
                </h2>
              </div>
              <Link to="/areas" data-testid="service-all-areas-link" className="inline-flex items-center gap-2 text-[#64748b] hover:text-[#9c7a4d] text-sm uppercase tracking-[0.2em] transition-colors">
                View all areas <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-3">
              {SUBURBS.map((s) => (
                <Link
                  key={s.slug}
                  to={`/tiling/${s.slug}`}
                  data-testid={`service-area-${s.slug}`}
                  className="text-sm text-[#475569] hover:text-white hover:bg-[#9c7a4d] border border-[#0f172a]/15 hover:border-[#9c7a4d] px-4 py-2 transition-colors"
                >
                  {cat.title} in {s.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <p className="overline !text-[#9c7a4d] mb-8">Other Specialities</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/services/${r.id}`}
                  data-testid={`related-${r.id}`}
                  className="group relative overflow-hidden aspect-[16/10] border border-[#0f172a]/10"
                >
                  <img src={r.image} alt={r.title} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover brightness-[0.6] group-hover:brightness-75 transition-[filter,transform] duration-700 group-hover:scale-105" />
                  <div className="relative z-10 h-full flex flex-col justify-end p-6">
                    <h3 className="font-serif text-2xl text-white flex items-center gap-2">
                      {r.title}
                      <ArrowRight className="h-4 w-4 text-[#c9a87c] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-12">
              <Link to="/" className="inline-flex items-center gap-2 text-[#64748b] hover:text-[#9c7a4d] text-sm uppercase tracking-[0.2em] transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back to home
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
}
